import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FundProjectionScreenOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { createProfit, deleteProfit, getProfitData, updateProfit } from '../services/profitService';
import { getAllAccounts, getAllStocks } from '../services/stockService';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const periodLabels = {
  day: '일',
  week: '주',
  month: '월',
  year: '년',
};

const Profits = () => {
  const [form] = Form.useForm();
  const [profits, setProfits] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totals, setTotals] = useState({ total_invested: 0, total_return: 0, return_rate: 0 });
  const [period, setPeriod] = useState('month');
  const [filters, setFilters] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProfit, setEditingProfit] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [stocks, setStocks] = useState([]);

  const formatCurrency = (value) => '₩' + new Intl.NumberFormat('ko-KR').format(Math.round(Number(value || 0)));
  const formatPercent = (value) => Number(value || 0).toFixed(2) + '%';

  const latestSummary = summary.length > 0 ? summary[summary.length - 1] : null;
  const displayedSummary = useMemo(() => [...summary].reverse(), [summary]);
  const profitTone = Number(totals.total_return || 0) >= 0 ? '#52c41a' : '#ff4d4f';
  const returnTone = Number(totals.return_rate || 0) >= 0 ? '#52c41a' : '#ff4d4f';
  const accountTitle = accounts[0]?.account_number || accounts[0]?.account_name || '전체 계좌';
  const accountSubtitle = accounts[0]?.account_name || '계좌별 수익 데이터를 통합 조회합니다.';
  const rangeStart = summary[0]?.period || dateRange?.[0]?.format('YYYY.MM.DD') || '조회 시작';
  const rangeEnd = latestSummary?.period || dateRange?.[1]?.format('YYYY.MM.DD') || moment().format('YYYY.MM.DD');
  const latestAsset = Number(latestSummary?.total_invested || totals.total_invested || 0) + Number(latestSummary?.total_return || 0);

  const overviewItems = [
    { label: '투자원금', value: formatCurrency(totals.total_invested) },
    { label: '총 손익', value: formatCurrency(totals.total_return), color: profitTone },
    { label: '총 수익률', value: formatPercent(totals.return_rate), color: returnTone },
    { label: '최근 기말자산', value: formatCurrency(latestAsset) },
    { label: '집계 항목', value: new Intl.NumberFormat('ko-KR').format(profits.length) },
    { label: '조회 단위', value: periodLabels[period] + ' 단위' },
  ];

  const chartOptions = useMemo(() => ({
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Open Sans, Helvetica, Arial, sans-serif',
    },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f0f0f0', strokeDashArray: 4 },
    plotOptions: { bar: { columnWidth: '42%', borderRadius: 4 } },
    stroke: { width: [0, 3], curve: 'smooth' },
    fill: { opacity: [0.9, 1] },
    legend: { position: 'bottom', horizontalAlign: 'center', markers: { radius: 2 } },
    xaxis: {
      categories: summary.map(item => item.period),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: '#8c8c8c' } },
    },
    yaxis: [
      {
        title: { text: '손익' },
        labels: { formatter: value => formatCurrency(value).replace('₩', '') },
      },
      {
        opposite: true,
        title: { text: '기말자산' },
        labels: { formatter: value => formatCurrency(value).replace('₩', '') },
      },
    ],
    colors: ['#52c41a', '#141414'],
    tooltip: { y: { formatter: value => formatCurrency(value) } },
  }), [summary]);

  const chartSeries = useMemo(() => ([
    { name: '기간 손익', type: 'column', data: summary.map(item => item.total_return || 0) },
    {
      name: '기말자산',
      type: 'line',
      data: summary.map(item => Number(item.total_invested || 0) + Number(item.total_return || 0)),
    },
  ]), [summary]);

  const fetchMeta = async () => {
    try {
      const [accountData, stockData] = await Promise.all([getAllAccounts(), getAllStocks()]);
      setAccounts(accountData.results || []);
      setStocks(stockData.results || []);
    } catch (error) {
      message.warning('계좌/종목 목록을 불러오지 못했습니다. 직접 입력은 가능합니다.');
    }
  };

  const fetchProfits = async (nextFilters = filters, nextPeriod = period) => {
    try {
      setLoading(true);
      const data = await getProfitData({ ...nextFilters, period: nextPeriod });
      setProfits(data.results || []);
      setSummary(data.summary || []);
      setTotals(data.totals || { total_invested: 0, total_return: 0, return_rate: 0 });
    } catch (error) {
      message.error('수익 데이터를 불러오지 못했습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    fetchProfits({}, 'month');
  }, []);

  const handlePeriodChange = (value) => {
    setPeriod(value);
    fetchProfits(filters, value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setFilters({
      ...filters,
      date_from: dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
      date_to: dates?.[1] ? dates[1].format('YYYY-MM-DD') : '',
    });
  };

  const resetFilters = () => {
    setFilters({});
    setDateRange(null);
    fetchProfits({}, period);
  };

  const openCreateModal = () => {
    setEditingProfit(null);
    form.resetFields();
    form.setFieldsValue({ profit_date: moment() });
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingProfit(record);
    form.setFieldsValue({
      account: record.account,
      stock: record.stock,
      profit_date: record.profit_date ? moment(record.profit_date) : null,
      total_invested: Number(record.total_invested),
      total_return: Number(record.total_return),
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        profit_date: values.profit_date.format('YYYY-MM-DD'),
      };

      if (editingProfit) {
        await updateProfit(editingProfit.id, payload);
        message.success('수익 항목이 수정되었습니다.');
      } else {
        await createProfit(payload);
        message.success('수익 항목이 추가되었습니다.');
      }
      setModalVisible(false);
      fetchProfits();
    } catch (error) {
      if (error.errorFields) return;
      message.error('저장 실패: ' + error.message);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteProfit(record.id);
      message.success('수익 항목이 삭제되었습니다.');
      fetchProfits();
    } catch (error) {
      message.error('삭제 실패: ' + error.message);
    }
  };

  const columns = [
    { title: '일자', dataIndex: 'profit_date', width: 110, render: date => moment(date).format('YYYY-MM-DD') },
    {
      title: '종목',
      dataIndex: 'stock_name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || record.stock}</Text>
          <Text type="secondary">{record.stock_symbol}</Text>
        </Space>
      ),
    },
    { title: '계좌', dataIndex: 'account_name', width: 140 },
    { title: '투자금', dataIndex: 'total_invested', align: 'right', render: formatCurrency },
    {
      title: '수익',
      dataIndex: 'total_return',
      align: 'right',
      render: value => <Text type={Number(value) >= 0 ? 'success' : 'danger'}>{formatCurrency(value)}</Text>,
    },
    {
      title: '수익률',
      key: 'return_rate',
      align: 'right',
      render: (_, record) => {
        const invested = Number(record.total_invested || 0);
        const profit = Number(record.total_return || 0);
        return invested ? formatPercent((profit / invested) * 100) : '-';
      },
    },
    {
      title: '관리',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm title="삭제하시겠습니까?" okText="삭제" cancelText="취소" onConfirm={() => handleDelete(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const summaryColumns = [
    { title: '조회일자', dataIndex: 'period', width: 120 },
    {
      title: '기말자산',
      key: 'ending_asset',
      align: 'right',
      render: (_, record) => formatCurrency(Number(record.total_invested || 0) + Number(record.total_return || 0)),
    },
    {
      title: '총손익',
      dataIndex: 'total_return',
      align: 'right',
      render: value => <Text type={Number(value) >= 0 ? 'success' : 'danger'}>{formatCurrency(value)}</Text>,
    },
    {
      title: '수익률',
      dataIndex: 'return_rate',
      align: 'right',
      render: value => <Text type={Number(value) >= 0 ? 'success' : 'danger'}>{formatPercent(value)}</Text>,
    },
  ];

  return (
    <div className="layout-content profits-page">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card className="profit-hero">
            <div className="profit-hero-top">
              <div className="profit-account">
                <span className="profit-account-icon"><FundProjectionScreenOutlined /></span>
                <div>
                  <Title level={3} className="profit-account-title">수익 및 자산 추이</Title>
                  <div className="profit-account-subtitle">{accountTitle} · {accountSubtitle}</div>
                </div>
              </div>
              <div className="profit-actions">
                <Select value={period} onChange={handlePeriodChange} style={{ width: 120 }}>
                  {Object.keys(periodLabels).map(key => <Option key={key} value={key}>{periodLabels[key]} 단위</Option>)}
                </Select>
                <Button icon={<ReloadOutlined />} onClick={() => fetchProfits()} loading={loading}>새로고침</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>항목 추가</Button>
              </div>
            </div>
            <div className="profit-overview">
              {overviewItems.map(item => (
                <div className="profit-overview-item" key={item.label}>
                  <div className="profit-overview-label">{item.label}</div>
                  <div className="profit-overview-value" style={{ color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col span={24}>
          <Card>
            <div className="profit-toolbar">
              <Radio.Group value={period} onChange={event => handlePeriodChange(event.target.value)} buttonStyle="solid">
                {Object.keys(periodLabels).map(key => <Radio.Button key={key} value={key}>{periodLabels[key]}별</Radio.Button>)}
              </Radio.Group>
              <div className="profit-filter-row">
                <Input placeholder="종목명" value={filters.stock_name || ''} onChange={e => setFilters({ ...filters, stock_name: e.target.value })} style={{ width: 160 }} />
                <Input placeholder="종목코드" value={filters.stock_symbol || ''} onChange={e => setFilters({ ...filters, stock_symbol: e.target.value })} style={{ width: 130 }} />
                <RangePicker value={dateRange} onChange={handleDateRangeChange} />
                <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchProfits()}>검색</Button>
                <Button onClick={resetFilters}>초기화</Button>
              </div>
            </div>
            <Chart options={chartOptions} series={chartSeries} type="line" height={340} />
            <div className="profit-chart-range"><span>{rangeStart}</span><span>{rangeEnd}</span></div>
          </Card>
        </Col>

        <Col span={24}>
          <Card className="profit-table-card" title="기간별 자산 및 손익">
            <Table rowKey="period" columns={summaryColumns} dataSource={displayedSummary} pagination={{ pageSize: 10 }} size="middle" scroll={{ x: 760 }} />
          </Card>
        </Col>

        <Col span={24}>
          <Card title="수익 항목 관리">
            <Table rowKey="id" columns={columns} dataSource={profits} loading={loading} pagination={{ pageSize: 15, showSizeChanger: true }} scroll={{ x: 900 }} />
          </Card>
        </Col>
      </Row>

      <Modal title={editingProfit ? '수익 항목 수정' : '수익 항목 추가'} visible={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} okText="저장" cancelText="취소">
        <Form form={form} layout="vertical">
          <Form.Item name="profit_date" label="수익 일자" rules={[{ required: true, message: '수익 일자를 선택하세요.' }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="account" label="계좌" rules={[{ required: true, message: '계좌를 선택하세요.' }]}>
            <Select showSearch optionFilterProp="children" placeholder="계좌 선택">
              {accounts.map(account => <Option key={account.id} value={account.id}>{account.account_name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="stock" label="종목" rules={[{ required: true, message: '종목을 선택하세요.' }]}>
            <Select showSearch optionFilterProp="children" placeholder="종목 선택">
              {stocks.map(stock => <Option key={stock.id} value={stock.id}>{stock.stock_name} ({stock.stock_symbol})</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="total_invested" label="투자금" rules={[{ required: true, message: '투자금을 입력하세요.' }]}><InputNumber min={0} style={{ width: '100%' }} formatter={value => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/,/g, '')} /></Form.Item>
          <Form.Item name="total_return" label="수익" rules={[{ required: true, message: '수익을 입력하세요.' }]}><InputNumber style={{ width: '100%' }} formatter={value => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/,/g, '')} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profits;
